// Strategies.tsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useStrategiesStore } from "@/stores/strategiesStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { api } from "@/lib/api";
import { StrategyType } from "@/lib/types";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

interface StrategyFormValues {
  name: string;
  description?: string;
  call_type: "dialin" | "dialout";
  language?: "en" | "es";
  strategy_type?: StrategyType | "none";
  medium?: "phone" | "sms";
}

export const LANGUAGE_MAP: Record<string, string> = {
  en: "English",
  es: "Spanish",
  zh: "Chinese",
  hi: "Hindi",
  vi: "Vietnamese",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  ru: "Russian",
};

export const DEFAULT_LANGUAGE = "en";

const Strategies: React.FC = () => {
  const navigate = useNavigate();
  const { selectedOrganizationId } = useOrganizationsStore();
  const {
    strategiesByOrg,
    setStrategies,
    fetchStrategiesForCurrentOrg,
    addStrategy,
  } = useStrategiesStore();

  const [open, setOpen] = React.useState(false);

  const form = useForm<StrategyFormValues>({
    defaultValues: {
      name: "",
      description: "",
      call_type: "dialin",
      language: "en",
      medium: "phone",
    },
  });

  useEffect(() => {
    if (selectedOrganizationId) {
      fetchStrategiesForCurrentOrg();
    }
  }, [selectedOrganizationId]);

  const handleStrategyClick = (strategyId: string) => {
    navigate(`/strategy/${strategyId}/`);
  };

  const strategies = selectedOrganizationId
    ? [...(strategiesByOrg[selectedOrganizationId] || [])].reverse()
    : [];

  const onSubmit = async (data: StrategyFormValues) => {
    if (!selectedOrganizationId) {
      console.error("No organization selected");
      return;
    }
    try {
      const payload = {
        ...data,
        organization_id: selectedOrganizationId,
        strategy_type:
          data.strategy_type === "none" ? null : data.strategy_type,
      };
      const newStrategy = await api.createStrategy(payload);
      addStrategy(selectedOrganizationId, newStrategy);
      form.reset();
      setOpen(false);
    } catch (error) {
      console.error("Failed to create strategy:", error);
    }
  };

  if (!selectedOrganizationId) {
    return <div className="text-center">Please select an organization</div>;
  }

  return (
    <div className="m-8">
      <Card className="w-full">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle>Strategies</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>Create Strategy</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a New Strategy</DialogTitle>
                <DialogDescription>
                  Enter the details for the new strategy.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    rules={{ required: "Name is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Strategy Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input placeholder="Description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="call_type"
                    rules={{ required: "Call type is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Call Type</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a call type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="dialin">Dial-in</SelectItem>
                              <SelectItem value="dialout">Dial-out</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="language"
                    // rules={{ required: "Language is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Language</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a language" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(LANGUAGE_MAP).map(
                                ([code, name]) => (
                                  <SelectItem key={code} value={code}>
                                    {name}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="strategy_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Strategy Type (Optional)</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value || ""}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a strategy type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value={StrategyType.CONSENT}>
                                Consent
                              </SelectItem>
                              <SelectItem value={StrategyType.CHECK_IN}>
                                Check In
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="medium"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Medium</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a medium" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="phone">Phone</SelectItem>
                              <SelectItem value="sms">SMS</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <DialogFooter>
                    <Button type="submit">Create</Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Call Type</TableHead>
                <TableHead>Medium</TableHead>
                <TableHead>Strategy Type</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Language</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {strategies.length > 0 ? (
                strategies.map((strategy) => (
                  <TableRow
                    key={strategy.id}
                    onClick={() => handleStrategyClick(strategy.id)}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    <TableCell>{strategy.name}</TableCell>
                    <TableCell>{strategy.description}</TableCell>
                    <TableCell>{strategy.call_type}</TableCell>
                    <TableCell>{strategy.medium ?? "phone"}</TableCell>
                    <TableCell>
                      {strategy.strategy_type
                        ? strategy.strategy_type
                            .replace("_", " ")
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {new Date(strategy.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {LANGUAGE_MAP[strategy.language ?? DEFAULT_LANGUAGE] ||
                        DEFAULT_LANGUAGE}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    No strategies available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Strategies;
